import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Form,
  Input,
  Select,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import UserCreateModal from '../create/UserCreateModal';
import UserEditModal from '../edit/UserEditModal';
import UserViewModal from '../view/UserViewModal';
import UserDeleteModal from '../delete/UserDeleteModal';
import WithdrawPointsModal from '../withdraw/WithdrawPointsModal';
import './UserList.css';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : '—';
};

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const STATUS_OPTIONS = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

export default function UserList() {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [modalMode, setModalMode] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [withdrawUser, setWithdrawUser] = useState(null);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [viewBalance, setViewBalance] = useState(null);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get('/users/roles');
      const roles = res.data?.roles ?? [];
      setRoleOptions(roles.map((r) => ({ value: r.value, label: r.label })));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load roles';
      dispatch(setMessage({ message: msg, type: 'error' }));
    }
  }, []);

  const fetchUsers = useCallback(
    async (page, limit, search) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (search && search.trim()) params.search = search.trim();
        const res = await axios.get('/users', { params });
        setUsers(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to load users';
        dispatch(setMessage({ message: msg, type: 'error' }));
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const refetch = useCallback(() => {
    fetchUsers(pagination.current, pagination.pageSize, searchApplied);
  }, [fetchUsers, pagination.current, pagination.pageSize, searchApplied]);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize, searchApplied);
  }, [pagination.current, pagination.pageSize, searchApplied, fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchApplied(searchInput.trim());
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const openCreateModal = () => {
    setSelectedUser(null);
    setModalMode('create');
    form.resetFields();
    form.setFieldsValue({ roles: ['read-only'], status: true });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    form.setFieldsValue({
      name: user.name ?? '',
      email: user.email ?? '',
      roles: Array.isArray(user.roles) ? user.roles : [],
      status: user.status !== false,
      newPassword: '',
      confirmPassword: '',
    });
  };

  const openViewModal = (user) => {
    setSelectedUser(user);
    setModalMode('view');
    setViewBalance(null);
    if (user?._id) {
      (async () => {
        try {
          const res = await axios.get(`/users/${user._id}/points`);
          if (res.data?.balance !== undefined) setViewBalance(res.data.balance);
        } catch (err) {
          // Balance fetch failed; view still opens without balance
        }
      })();
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedUser(null);
    form.resetFields();
  };

  const handleCreateOrEdit = async () => {
    if (modalMode === 'view') {
      closeModal();
      return;
    }
    try {
      const values = await form.validateFields();
      if (modalMode === 'edit' && selectedUser?._id) {
        setSubmitLoading(true);
        const payload = {
          name: values.name?.trim() ?? '',
          email: values.email?.trim() ?? '',
          roles: values.roles ?? [],
          status: values.status !== false,
        };
        if (values.newPassword?.trim()) {
          payload.newPassword = values.newPassword.trim();
        }
        await axios.put(`/users/${selectedUser._id}`, payload);
        dispatch(setMessage({ message: 'User updated successfully', type: 'success' }));
        closeModal();
        refetch();
      } else {
        if (!values.password?.trim()) {
          dispatch(setMessage({ message: 'Password is required', type: 'error' }));
          return;
        }
        setSubmitLoading(true);
        await axios.post('/users', {
          name: values.name?.trim() ?? '',
          email: values.email?.trim() ?? '',
          password: values.password,
          roles: values.roles ?? ['read-only'],
          status: values.status !== false,
        });
        dispatch(setMessage({ message: 'User created successfully', type: 'success' }));
        closeModal();
        refetch();
      }
    } catch (err) {
      const msg = err.response?.data?.error || (err.errorFields ? 'Please fix the form' : 'Request failed');
      if (err.response?.data?.error) dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/users/${userToDelete._id}`);
      dispatch(setMessage({ message: 'User deleted successfully', type: 'success' }));
      setUserToDelete(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openWithdrawModal = (user) => {
    setWithdrawUser(user);
    setModalMode(null);
    setSelectedUser(null);
  };

  const handleWithdrawSubmit = async ({ points, notes }) => {
    if (!withdrawUser?._id) return;
    setWithdrawLoading(true);
    try {
      await axios.post(`/users/${withdrawUser._id}/withdraw-points`, { points, notes });
      dispatch(setMessage({ message: `Withdrew ${points} points successfully`, type: 'success' }));
      setWithdrawUser(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Withdraw failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
      throw err;
    } finally {
      setWithdrawLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span className="user-list-cell-name">{name || '—'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => <span className="user-list-cell-email">{email}</span>,
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => (
        <>
          {Array.isArray(roles) && roles.length > 0
            ? roles.map((r) => (
                <Tag key={r} className="user-list-tag-role">
                  {roleOptions.find((o) => o.value === r)?.label ?? r}
                </Tag>
              ))
            : '—'}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag className={status !== false ? 'user-list-tag-status-active' : 'user-list-tag-status-inactive'}>
          {status !== false ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (val) => formatDateTime(val),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              className="app-btn-delete"
              onClick={() => handleDeleteClick(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="user-list-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="user-list-header">
          <div className="user-list-header-left">
            <Title level={3} style={{ margin: 0 }}>
              Users
            </Title>
            <Input
              placeholder="Search by name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              allowClear
              className="user-list-search-input"
            />
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="user-list-add-btn app-btn-primary"
          >
            Add user
          </Button>
        </div>

        <Card className="user-list-card">
          <Table
            className="user-list-table"
            columns={columns}
            dataSource={users.map((row) => ({ ...row, key: row._id }))}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} users`,
              size: 'default',
              responsive: true,
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            size="middle"
            tableLayout="auto"
            scroll={{ x: 720 }}
          />
        </Card>
      </Space>

      <UserCreateModal
        open={modalMode === 'create'}
        onCancel={closeModal}
        form={form}
        onSubmit={handleCreateOrEdit}
        submitLoading={submitLoading}
        roleOptions={roleOptions}
      />

      <UserEditModal
        open={modalMode === 'edit'}
        onCancel={closeModal}
        form={form}
        onSubmit={handleCreateOrEdit}
        submitLoading={submitLoading}
        roleOptions={roleOptions}
      />

      <UserViewModal
        open={modalMode === 'view'}
        onCancel={closeModal}
        user={selectedUser}
        roleOptions={roleOptions}
        formatDateTime={formatDateTime}
        balance={viewBalance}
        onWithdrawPoints={() => openWithdrawModal(selectedUser)}
      />

      <UserDeleteModal
        open={userToDelete}
        onCancel={() => setUserToDelete(null)}
        onConfirm={handleDeleteConfirm}
        user={userToDelete}
        loading={deleteLoading}
      />

      <WithdrawPointsModal
        open={!!withdrawUser}
        onCancel={() => setWithdrawUser(null)}
        user={withdrawUser}
        onSubmit={handleWithdrawSubmit}
        submitLoading={withdrawLoading}
      />
    </div>
  );
}
